import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload, Trash2, RefreshCw, FileText, Image, Film, Table,
  Download, Search, CheckSquare, Square, File, Loader2
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 512 * 1024; // 512KB chunks

function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.startsWith('video/')) return Film;
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel') || contentType?.includes('csv')) return Table;
  return FileText;
}

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

async function compressImageClient(file, maxDim = 1920, quality = 0.75) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (Math.max(w, h) > maxDim) {
          const ratio = maxDim / Math.max(w, h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (blob && blob.size < file.size) {
            const compressed = new window.File([blob], file.name, { type: file.type });
            resolve(compressed);
          } else {
            resolve(file);
          }
        }, file.type, quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

export default function FileUpload() {
  const [searchParams] = useSearchParams();
  const propertyParam = searchParams.get('property') || '';
  const [files, setFiles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProperty, setFilterProperty] = useState(propertyParam);
  const fileInputRef = useRef(null);
  const updateInputRef = useRef(null);
  const [updatingFileId, setUpdatingFileId] = useState(null);

  // Upload settings from admin
  const [imageCompression, setImageCompression] = useState(false);
  const [chunkedUpload, setChunkedUpload] = useState(false);

  const token = localStorage.getItem('token');
  const agent = JSON.parse(localStorage.getItem('agent') || '{}');
  const isBroker = agent?.role === 'broker';
  const headers = { Authorization: `Bearer ${token}` };

  const fetchFiles = useCallback(async () => {
    try {
      const params = filterProperty ? { property_id: filterProperty, own_only: true } : { own_only: true };
      const res = await axios.get(`${API}/files`, { headers, params });
      setFiles(res.data);
    } catch (err) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [filterProperty]);

  const fetchProperties = useCallback(async () => {
    try {
      const endpoint = isBroker ? `${API}/broker/properties` : `${API}/agent/properties`;
      const res = await axios.get(endpoint, { headers });
      setProperties(res.data);
    } catch (err) {}
  }, [isBroker]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      setImageCompression(res.data.image_compression_enabled ?? false);
      setChunkedUpload(res.data.chunked_upload_enabled ?? false);
    } catch (err) {}
  }, []);

  useEffect(() => { fetchFiles(); fetchProperties(); fetchSettings(); }, [fetchFiles, fetchProperties, fetchSettings]);

  const isImageType = (type) => ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type);

  const uploadChunked = async (file, propertyId) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // Init
    const initForm = new FormData();
    initForm.append('filename', file.name);
    initForm.append('content_type', file.type || 'application/octet-stream');
    initForm.append('total_size', file.size.toString());
    initForm.append('total_chunks', totalChunks.toString());
    if (propertyId) initForm.append('property_id', propertyId);

    const initRes = await axios.post(`${API}/files/upload/init-chunk`, initForm, { headers });
    const uploadId = initRes.data.upload_id;

    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      const chunkForm = new FormData();
      chunkForm.append('upload_id', uploadId);
      chunkForm.append('chunk_index', i.toString());
      chunkForm.append('chunk', blob, `chunk_${i}`);

      await axios.post(`${API}/files/upload/chunk`, chunkForm, { headers });
      setUploadProgress(`${file.name}: chunk ${i + 1}/${totalChunks}`);
    }

    // Complete
    const completeForm = new FormData();
    completeForm.append('upload_id', uploadId);
    await axios.post(`${API}/files/upload/complete-chunk`, completeForm, { headers });
  };

  const handleUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    if (!uploadFiles.length) return;
    setUploading(true);
    setUploadProgress('');

    let successCount = 0;
    for (const rawFile of uploadFiles) {
      try {
        let file = rawFile;

        // Client-side image compression
        if (imageCompression && isImageType(file.type)) {
          setUploadProgress(`Compressing ${file.name}...`);
          const compressed = await compressImageClient(file);
          if (compressed.size < file.size) {
            const saved = ((1 - compressed.size / file.size) * 100).toFixed(0);
            toast.info(`${file.name}: compressed ${formatSize(file.size)} → ${formatSize(compressed.size)} (${saved}% saved)`);
            file = compressed;
          }
        }

        // Use chunked upload for large files when enabled
        if (chunkedUpload && file.size > CHUNK_SIZE) {
          setUploadProgress(`Uploading ${file.name} in chunks...`);
          await uploadChunked(file, filterProperty);
        } else {
          // Standard upload
          setUploadProgress(`Uploading ${file.name}...`);
          const formData = new FormData();
          formData.append('file', file);
          if (filterProperty) formData.append('property_id', filterProperty);
          await axios.post(`${API}/files/upload`, formData, { headers });
        }
        successCount++;
      } catch (err) {
        toast.error(`Failed: ${rawFile.name} — ${err.response?.data?.detail || 'Upload error'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded`);
      fetchFiles();
    }
    setUploading(false);
    setUploadProgress('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpdate = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !updatingFileId) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.put(`${API}/files/${updatingFileId}`, formData, { headers });
      toast.success('File updated');
      fetchFiles();
    } catch (err) {
      toast.error('Failed to update file');
    } finally {
      setUpdatingFileId(null);
      if (updateInputRef.current) updateInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/files/${fileId}`, { headers });
      toast.success('File deleted');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(fileId); return n; });
      fetchFiles();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected file(s)?`)) return;
    try {
      await axios.post(`${API}/files/bulk-delete`, { file_ids: Array.from(selectedIds) }, { headers });
      toast.success(`${selectedIds.size} file(s) deleted`);
      setSelectedIds(new Set());
      fetchFiles();
    } catch (err) {
      toast.error('Failed to delete files');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredFiles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredFiles.map(f => f.id)));
    }
  };

  const filteredFiles = files.filter(f => {
    if (searchQuery && !f.original_filename.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const propNameMap = {};
  properties.forEach(p => { propNameMap[p.id] = p.address; });

  return (
    <Layout>
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
          File Upload
        </h1>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#1A4D2E] text-white hover:bg-[#143D24] rounded-full"
            data-testid="upload-btn"
          >
            {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>
        </div>
        <input ref={fileInputRef} type="file" multiple onChange={handleUpload} style={{ display: 'none' }} />
        <input ref={updateInputRef} type="file" onChange={handleUpdate} style={{ display: 'none' }} />
      </div>

      {/* Upload progress */}
      {uploading && uploadProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-700" data-testid="upload-progress">
          <Loader2 size={14} className="inline animate-spin mr-2" />
          {uploadProgress}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-files"
          />
        </div>
        <Select
          value={filterProperty || "__all__"}
          onValueChange={(val) => setFilterProperty(val === "__all__" ? "" : val)}
          data-testid="filter-property"
        >
          <SelectTrigger className="h-9 w-[220px] px-3 rounded-md border border-stone-300 bg-white text-sm">
            <SelectValue placeholder="All Properties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Properties</SelectItem>
            {properties.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Select All + Bulk Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSelectAll}
          className="flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-[#1A4D2E]"
          data-testid="select-all-btn"
        >
          {selectedIds.size === filteredFiles.length && filteredFiles.length > 0 ? (
            <CheckSquare size={18} className="text-[#1A4D2E]" />
          ) : (
            <Square size={18} />
          )}
          Select All ({filteredFiles.length})
        </button>
        {selectedIds.size > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBulkDelete}
            data-testid="bulk-delete-btn"
            className="rounded-full"
          >
            <Trash2 size={14} className="mr-1" />
            Delete {selectedIds.size} Selected
          </Button>
        )}
      </div>

      {/* File List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 size={32} className="animate-spin mx-auto text-stone-400" />
        </div>
      ) : filteredFiles.length === 0 ? (
        <Card className="p-12 text-center">
          <File size={48} className="mx-auto mb-4 text-stone-300" />
          <p className="text-lg text-stone-600">No files uploaded yet</p>
          <p className="text-sm text-stone-400 mt-1">Click "Upload Files" to get started</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredFiles.map((f) => {
            const Icon = getFileIcon(f.content_type);
            const isSelected = selectedIds.has(f.id);
            return (
              <Card
                key={f.id}
                data-testid={`file-row-${f.id}`}
                className={`p-3 sm:p-4 transition-all ${isSelected ? 'ring-2 ring-[#1A4D2E] bg-[#1A4D2E]/5' : 'hover:shadow-md'}`}
              >
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleSelect(f.id)} className="shrink-0">
                    {isSelected ? (
                      <CheckSquare size={18} className="text-[#1A4D2E]" />
                    ) : (
                      <Square size={18} className="text-stone-400" />
                    )}
                  </button>
                  <div className="p-2 rounded-lg bg-stone-100 shrink-0">
                    <Icon size={20} className="text-stone-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-800 truncate">{f.original_filename}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-stone-500 mt-0.5">
                      <span>{formatSize(f.file_size)}</span>
                      {f.compressed && <span className="text-green-600">compressed</span>}
                      {(f.property_address || (f.property_id && propNameMap[f.property_id])) && (
                        <span className="truncate max-w-[200px]">{f.property_address || propNameMap[f.property_id]}</span>
                      )}
                      <span>{new Date(f.uploaded_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={f.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-[#1A4D2E]"
                      title="Download"
                    >
                      <Download size={16} />
                    </a>
                    <button
                      onClick={() => { setUpdatingFileId(f.id); updateInputRef.current?.click(); }}
                      className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-blue-600"
                      title="Replace file"
                      data-testid={`update-btn-${f.id}`}
                    >
                      <RefreshCw size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(f.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-stone-500 hover:text-red-600"
                      title="Delete"
                      data-testid={`delete-btn-${f.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </Layout>
  );
}
